import { computed } from 'vue'
import { ItemAttributes } from '@/modules/items/types/itemTypes'
import {
	getCheckoutClientDetails,
	trackProductAddedToCart,
} from '@/modules/checkout/helpers/checkoutUtils'
import { PackagesUuid, VenusPackageUuid } from '@/modules/checkout/enum/packagesUuid'
import { getDirectCheckoutUrl } from '@/modules/common/utils/linkUtils'
import { TrackingEvents } from '@/modules/common/utils/trackingEvents'
import { trackSegment } from '@/modules/analytics/track'
import { ResultDocument } from '@/modules/items/types/resultsTypes'
import { daysAgo } from '@/modules/common/utils/dateUtils'
import { useStore } from 'vuex'
import { Client } from '@/modules/auth/types/authTypes'

export default function useTelehealth() {
	const TELEHEALTH_PAID_PRODUCT_LAUNCH_DATE = new Date('2022-07-07')
	const DAYS_AFTER_RESULT_IS_OLD = 90

	const store = useStore()

	const client = computed<Client>(() => {
		return store.state.auth.user?.client
	})

	const isAuthorizedUser = computed<boolean>(() => {
		return !!store.state.auth.user?.has_authorized_user_role
	})

	/** `parent_user.client` is a flat summary; the client's own record nests under `attributes`. */
	const consultationClient = computed(() => {
		return isAuthorizedUser.value
			? store.state.auth.user?.parent_user?.client
			: client.value?.attributes
	})

	const latestAnalysis = computed<ItemAttributes>(() => {
		return consultationClient.value?.latest_analysis
	})

	const beforeLaunchingPaidProduct = computed<boolean>(() => {
		return new Date(latestAnalysis.value?.created_at) < TELEHEALTH_PAID_PRODUCT_LAUNCH_DATE
	})

	const hasActiveConsultation = computed<boolean>(() => {
		return consultationClient.value?.has_active_fertility_consultation
	})

	/** An authorized user has no billing relationship with Legacy, so they can only schedule. */
	const canBookConsultation = computed<boolean>(() => {
		return !isAuthorizedUser.value
	})

	const canScheduleConsultation = computed<boolean>(() => {
		return beforeLaunchingPaidProduct.value || hasActiveConsultation.value
	})

	/**
	 * Buy-a-consultation checkout, with the client's details prefilled. Computed rather
	 * than a constant so it's built from the auth user as it stands when the button
	 * renders — at module-evaluation time that user isn't loaded yet.
	 */
	const fertilityConsultationCheckoutUrl = computed<string>(() => {
		return getDirectCheckoutUrl(
			VenusPackageUuid.FERTILITY_CONSULTATION_UUID,
			getCheckoutClientDetails(),
		)
	})

	function onBookNowClick() {
		trackProductAddedToCart(
			PackagesUuid.FERTILITY_CONSULTATION_UUID,
			TrackingEvents.PRODUCT_ADDED.RESULTS.location,
		)
	}

	async function getLink(): Promise<void> {
		trackSegment(TrackingEvents.SCHEDULE_CONSULTATION.RESULTS)
		window.location.href = '/schedule-results-call'
	}

	const latestReadyResult = computed<ResultDocument>(() => {
		return isAuthorizedUser.value
			? store.state.auth.user?.parent_user?.client?.latestReadyResult
			: client.value?.relationships?.latest_ready_result
	})

	const oldResult = computed<boolean>(() => {
		return new Date(latestReadyResult.value?.ready_at) < daysAgo(DAYS_AFTER_RESULT_IS_OLD)
	})

	async function setHasActiveFertilityConsultation(value: boolean) {
		await store.commit('auth/updateClientValue', {
			key: 'has_active_fertility_consultation',
			value: value,
		})
	}

	async function setBeforeLaunchingPaidProduct(value: boolean) {
		await store.commit('auth/setLatestAnalysisValue', {
			key: 'created_at',
			value: value ? new Date('2022-07-06') : new Date('2022-07-08'),
		})
	}

	async function setOldResult(value: boolean) {
		await store.commit('auth/setLatestResultValue', {
			key: 'ready_at',
			value: value ? daysAgo(DAYS_AFTER_RESULT_IS_OLD) : new Date(),
		})
	}

	return {
		onBookNowClick,
		getLink,
		beforeLaunchingPaidProduct,
		hasActiveConsultation,
		canBookConsultation,
		canScheduleConsultation,
		fertilityConsultationCheckoutUrl,
		oldResult,
		setHasActiveFertilityConsultation,
		setBeforeLaunchingPaidProduct,
		setOldResult,
		DAYS_AFTER_RESULT_IS_OLD,
	}
}
